import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_STRATEGY,
  DELETE_STRATEGY,
  FIND_ALL_STRATEGIES_BY_LOCATION,
  UPDATE_STRATEGY,
} from '../../queries';
import { Button } from '../../../../components';

import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import { getStatusEmoji, tableSortingMethods } from '../../../../utils';
import FinancialData from '../../../../components/common/FinancialData';
import { useUserContext } from '../../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../../components/error-handling';
import { ExportColumn } from '../../../../interfaces/ExportColumn';

export type FindAllStrategiesByLocation = {
  grossRevenueExposure: number;
  lengthOfDisruption: number;
  mitigation: number;
  netRevenueExposure: number;
  strategyAge: number;
  uuid: string;
  bcpStatus: string;
  lastUpdate: string;
  isDefault: boolean;
};

interface DataReturned {
  findAllStrategiesByLocation: FindAllStrategiesByLocation[];
}

interface StrategyTableProps {
  locationId: string;
  locationName: string;
}

export function StrategiesTable({
  locationId,
  locationName,
}: StrategyTableProps) {
  const navigate = useNavigate();

  const { canViewFinancials } = useUserContext();

  const { data, loading, error } = useQuery(FIND_ALL_STRATEGIES_BY_LOCATION, {
    variables: { uuid: locationId },
  });

  const strategiesData = data?.findAllStrategiesByLocation || [];

  const [createStrategy] = useMutation(CREATE_STRATEGY, {
    refetchQueries: ['FindAllStrategiesByLocation'],
  });

  const [updateStrategy] = useMutation(UPDATE_STRATEGY, {
    refetchQueries: ['FindAllStrategiesByLocation'],
  });

  const [DeleteStrategy] = useMutation(DELETE_STRATEGY, {
    refetchQueries: ['FindAllStrategiesByLocation'],
  });

  const columns: ColumnsType<any> = [
    {
      key: 'lengthOfDisruption',
      title: 'Length of Disruption (months)',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.lengthOfDisruption, b.lengthOfDisruption),
      render: (_, { lengthOfDisruption }) => (
        <span className="custom-table--column !font-normal">
          {lengthOfDisruption}
        </span>
      ),
    },
    {
      key: 'grossRevenueExposure',
      title: 'Total Revenue Exposure',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.grossRevenueExposure,
              b.grossRevenueExposure,
            )
        : undefined,
      render: (_, { grossRevenueExposure }) => (
        <span className="custom-table--column">
          {<FinancialData text={grossRevenueExposure} />}
        </span>
      ),
    },
    {
      key: 'mitigation',
      title: 'Mitigation',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.mitigation, b.mitigation)
        : undefined,
      render: (_, { mitigation }) => (
        <span className="custom-table--column">
          {<FinancialData text={mitigation} />}
        </span>
      ),
    },
    {
      key: 'netRevenueExposure',
      title: 'Net Revenue Exposure',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.netRevenueExposure,
              b.netRevenueExposure,
            )
        : undefined,
      render: (_, { netRevenueExposure }) => (
        <span className="custom-table--column">
          {<FinancialData text={netRevenueExposure} />}
        </span>
      ),
    },
    {
      key: 'strategyAge',
      title: 'Strategy Age (months)',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.strategyAge, b.strategyAge),
      render: (_, { strategyAge }) => (
        <span className="custom-table--column">{strategyAge}</span>
      ),
    },
    {
      key: 'bcpStatus',
      title: 'Strategy Status',
      width: 150,
      align: 'center',
      sorter: (a, b) => tableSortingMethods.string(a.bcpStatus, b.bcpStatus),
      render: (_, { bcpStatus }) => (
        <span className="custom-table--column">
          {bcpStatus ? (
            `${getStatusEmoji(bcpStatus)}   ${bcpStatus}`
          ) : (
            <span className="placeholder-text">No available status</span>
          )}
        </span>
      ),
    },
  ];

  const extendedActions = (record: any) => {
    return (
      <div className="flex items-center justify-end gap-4">
        <Button
          key={`A${record.uuid}`}
          type="primary-rounded"
          className="text-sm min-w-[160px]"
          onClick={() =>
            navigate(
              `/strategy-summary/${locationId}/${record.uuid}?locationName=${locationName}&strategyLength=${record.lengthOfDisruption}`,
            )
          }
        >
          Review Strategy
        </Button>
      </div>
    );
  };

  const formFields = [
    {
      key: 'lengthOfDisruption',
      label: 'Strategy Duration',
      type: 'time',
      decoratorWord: 'months',
      isRequired: true,
    },
  ];

  const baseColumns: ExportColumn[] = [
    {
      key: 'lengthOfDisruption',
      header: 'Length of Disruption (months)',
      type: 'text',
    },
    {
      key: 'grossRevenueExposure',
      header: 'Total Revenue Exposure',
      type: 'money',
    },
    {
      key: 'mitigation',
      header: 'Mitigation',
      type: 'money',
    },
    {
      key: 'netRevenueExposure',
      header: 'Net Revenue Exposure',
      type: 'money',
    },
    {
      key: 'strategyAge',
      header: 'Strategy Age (months)',
      type: 'text',
    },
    {
      key: 'bcpStatus',
      header: 'Strategy Status',
      type: 'text',
    },
  ];

  const modifyVariables = ({ values, type }: ModifyVariablesArgs) => {
    if (type === 'create') {
      return {
        lengthOfDisruption: parseInt(values.lengthOfDisruption),
        location: { uuid: locationId },
      };
    }

    return {
      ...values,
      lengthOfDisruption: parseInt(values.lengthOfDisruption),
    };
  };

  return (
    <TableV3
      title="Strategy Inventory"
      columns={columns}
      data={strategiesData}
      loading={loading}
      error={error}
      editable
      createGQL={createStrategy}
      updateGQL={updateStrategy}
      deleteGQL={DeleteStrategy}
      formFields={formFields}
      extendActions={extendedActions}
      modifyVariables={modifyVariables}
      editableOptions={{
        create: canViewFinancials,
        update: canViewFinancials,
        delete: canViewFinancials,
      }}
      exportExcelColumns={baseColumns}
    />
  );
}
